import { CognitoIdentityClient } from "@aws-sdk/client-cognito-identity";
import { CognitoIdentityCredentialProvider, CognitoIdentityCredentials, fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";

class TemporaryCredentials {
  private client: CognitoIdentityClient;
  private provider: CognitoIdentityCredentialProvider | null = null
  private inFlight: Promise<CognitoIdentityCredentials> | null = null;

  constructor() {
    this.client = new CognitoIdentityClient({ region: "eu-west-1" });

    this.provider = fromCognitoIdentityPool({
      identityPoolId: process.env.GATSBY_COGNITO_IDENTITY_POOL_ID!,
      client: this.client
    })
  }

  async generate(retries = 3, delay = 1000): Promise<CognitoIdentityCredentials> {
    // A. If a request is already running, join it! Don't start a new one.
    if (this.inFlight) {
      return this.inFlight;
    }

    try {
      if (!this.provider) throw new Error("Provider failed to init");

      // B. Start a new request and save the PROMISE to the module-level variable
      this.inFlight = this.provider();

      // Wait for it to resolve
      const credentials = await this.inFlight;

      this.store(credentials);

      return credentials;
    } catch (e) {
      // If it fails, clear the promise so we can try again
      this.inFlight = null;

      if (retries > 0) {
        await new Promise((resolve) => setTimeout(resolve, delay));
        return this.generate(retries - 1, delay * 2);
      }
      throw e;
    } finally {
      // C. Clear the promise. 
      // The AWS provider has its own internal cache for *expiration*, so next time
      // we call it, it will return instantly from memory. We only needed this
      // inflightPromise to handle the millisecond-level race condition.
      this.inFlight = null;
    }
  }

  private store(credentials: CognitoIdentityCredentials) {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(`${process.env.GATSBY_APP_KEY ?? 'prod'}-temporary-credentials`, JSON.stringify(credentials));
    }
  }

  retrieve(): CognitoIdentityCredentials | null {
    if (typeof window === "undefined") return null;

    const credentials = sessionStorage.getItem(
      `${process.env.GATSBY_APP_KEY ?? 'prod'}-temporary-credentials`
    );

    if (!credentials) return null;

    return JSON.parse(credentials);
  }
}

export default new TemporaryCredentials();